import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Car, Plus, Search, X, AlertTriangle } from 'lucide-react';
import { CustomerVehicle } from '../../lib/customers';

interface SelectVehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (vehicle: CustomerVehicle) => void;
  vehicles: CustomerVehicle[];
  selectedVehicles?: CustomerVehicle[];
  onAddVehicle: () => void;
}

export default function SelectVehicleModal({
  isOpen,
  onClose,
  onSelect,
  vehicles,
  selectedVehicles = [],
  onAddVehicle,
}: SelectVehicleModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [localVehicles, setLocalVehicles] = useState<CustomerVehicle[]>([]);

  React.useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setLocalVehicles(vehicles);
    }
  }, [isOpen, vehicles]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setLocalVehicles([]);
      setSearchQuery('');
    }
  };

  const handleAddVehicle = () => {
    onAddVehicle(); // Let parent handle both closing this modal and opening create modal
  };

  const filteredVehicles = localVehicles.filter(vehicle => {
    const searchLower = searchQuery.toLowerCase();
    return (
      `${vehicle.make} ${vehicle.model}`.toLowerCase().includes(searchLower) ||
      vehicle.color?.toLowerCase().includes(searchLower) ||
      vehicle.license_plate?.toLowerCase().includes(searchLower) ||
      vehicle.vin?.toLowerCase().includes(searchLower)
    );
  });

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      onClick={onClose}
      onTransitionEnd={handleTransitionEnd}
      style={{
        zIndex: 100300,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100310
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
            <h2 className="text-2xl font-bold text-gray-900" style={{ 
              letterSpacing: '-0.02em'
            }}>
              Select Vehicle
            </h2>
          </div>

          {/* Search and Add Vehicle Section */}
          <div className="bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search vehicles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 w-full"
                  style={{ fontSize: '16px' }}
                />
              </div>
              <button
                onClick={handleAddVehicle}
                className="inline-flex items-center justify-center px-4 py-3 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm whitespace-nowrap"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Plus className="h-4 w-4 mr-1.5" />
                Add Vehicle
              </button>
            </div>
          </div>

          {/* Content Area */}
          <div 
            className="flex-1 overflow-y-auto bg-white"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
            }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-3`} style={{
              paddingBottom: isMobile ? 'calc(1rem + env(safe-area-inset-bottom, 0px))' : '1rem'
            }}>
              {filteredVehicles.map((vehicle) => {
                const isSelected = selectedVehicles.some(v => v.id === vehicle.id);
                return (
                  <div
                    key={vehicle.id}
                    onClick={() => {
                      if (isSelected) return;
                      onSelect(vehicle);
                    }}
                    className={`bg-white rounded-[12px] border border-gray-200/60 p-2 relative transition-all duration-150 shadow-[0_1px_2px_rgba(0,0,0,0.05)] ${
                      isSelected
                        ? 'opacity-60 cursor-not-allowed'
                        : 'cursor-pointer active:scale-[0.98] touch-manipulation'
                    }`}
                    style={{ 
                      WebkitTapHighlightColor: 'transparent',
                      borderColor: isSelected ? 'rgba(251, 146, 60, 0.6)' : undefined
                    }}
                  >
                    {/* Top row: Vehicle name */}
                    <div className="flex items-center space-x-2.5 mb-2">
                      <div className="h-6 w-6 rounded-full bg-gray-900 flex items-center justify-center flex-shrink-0">
                        <Car className="h-3.5 w-3.5 text-white" />
                      </div>
                      <div className="text-[14px] font-semibold text-gray-900 truncate leading-tight flex-1 min-w-0">
                        {vehicle.year} {vehicle.make} {vehicle.model}
                      </div>
                      {isSelected && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <AlertTriangle className="h-3 w-3 text-orange-500" />
                          <span className="text-[10px] font-semibold text-orange-600">Already added</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Dividing line */}
                    <div className="border-t border-gray-200/60 my-2"></div>
                    
                    {/* Vehicle details */}
                    <div className="space-y-1.5 pt-1">
                      {vehicle.color && (
                        <div className="text-sm text-gray-600">
                          {vehicle.color}
                        </div>
                      )}
                      {vehicle.license_plate && (
                        <div className="text-sm text-gray-600">
                          License: {vehicle.license_plate}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

            </div>
            
            {vehicles.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Car className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No vehicles</h3>
                <p className="text-sm text-gray-600 mb-6">
                  {searchQuery ? 'Try adjusting your search terms' : 'Add a vehicle to get started'}
                </p>
                <button
                  onClick={handleAddVehicle}
                  className="inline-flex items-center justify-center px-4 py-3 text-sm font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 border border-blue-200 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Vehicle
                </button>
              </div>
            ) : filteredVehicles.length === 0 && searchQuery && (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Car className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No matching vehicles</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Try adjusting your search terms
                </p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="inline-flex items-center justify-center px-4 py-3 text-sm font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 border border-blue-200 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  Clear search
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}